import { RouterView, createRouter, createWebHistory } from 'vue-router'
import { h } from 'vue'
import ServiceModeLayout from '@/layouts/ServiceModeLayout.vue'
import SettingsView from '@/views/SettingsView.vue'
import ManualReportingView from '../views/ManualReportingView.vue'
import AccountView from '../views/AccountView.vue'
import AuthCallbackView from '@/views/AuthCallbackView.vue'
import { useUserStore } from '@/stores/userStore'
import { useInsightsStore } from '@/stores/insightsStore'
import config, { loadConfig } from '@/services/ConfigService'

const router = createRouter({
  // linkActiveClass: 'border-l-4 border-white rounded-4 bg-slate-500 rounded',
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      component: () => import('../layouts/MainLayout.vue'),
      beforeEnter: async (to) => {
        if (to.path !== '/') return true
        
        else {
          const userStore = useUserStore()
          await loadConfig()

          let startPage = userStore.startPageName

          // Ensure that the start view is not a disabled feature
          // If the user's start page is a feature that can be toggled, check that it's enabled
          if (startPage in config.features) {
            const isEnabled = !!config.features[startPage as keyof typeof config.features]
            if (!isEnabled) {
              startPage = 'groups'
            }
          }

          return { name: startPage }
        }
      },
      children: [
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import('../views/DashboardView.vue')
        },
        {
          path: 'groups',
          component: h(RouterView),
          children: [
            {
              path: '',
              name: 'groups',
              component: () => import('../views/GroupsView.vue'),
            },
            {
              path: ':groupId/integration',
              name: 'integration',
              component: () => import('../views/IntegrationView.vue'),
              // beforeEnter: checkIsAdmin
            },
            {
              path: ':groupId/integration/create-importer',
              name: 'importer-add',
              component: () => import('../views/ImporterCreateView.vue')
            },
            {
              path: ':groupId/shops/:shopId',
              name: 'shop-detail',
              component: () => import('../views/ShopDetailView.vue')
            },
            {
              path: ':groupId',
              name: 'group-detail',
              component: () => import('../views/GroupDetailView.vue')
            },
          ]
        },
        {
          path: 'insights',
          component: h(RouterView),
          beforeEnter: async () => {
            const userStore = useUserStore()
            const insightsStore = useInsightsStore()
            insightsStore.loadGlobalSelectionFromLocalStorage()
            await loadConfig()

            // Module should be enabled or user must be admin
            if (!config.features.insights && !userStore.isSuperAdmin) {
              return { name: 'error', query: {
                status: 403,
                title: 'Feature not available',
                description: 'This feature is currently not enabled for your account. Please contact your administrator if you believe you should have access to this functionality.'
              }}
            }
          },
          children: [
            {
              path: '',
              name: 'insights',
              component: () => import('../views/InsightsView.vue')
            },
            {
              path: 'dashboards/:dashboardId',
              name: 'insights-dashboard-detail',
              component: () => import('../views/InsightsDashboardDetailView.vue')
            },
            {
              path: 'charts/:chartId',
              name: 'insights-chart-detail',
              component: () => import('../views/InsightsChartDetailView.vue')
            },
            {
              path: 'charts/create',
              name: 'insights-chart-create',
              component: () => import('../views/InsightsChartCreateView.vue')
            },
            {
              path: 'charts',
              name: 'insights-charts',
              component: () => import('../views/InsightsChartsView.vue')
            },
            {
              path: 'insights-explorer',
              name: 'insights-explorer',
              component: () => import('../views/InsightsExplorerView.vue')
            }
          ]
        },
        {
          path: 'activity-feed',
          name: 'activity-feed',
          component: () => import('../views/ActivitiesView.vue')
        },
        {
          path: 'sales',
          name: 'sales',
          component: () => import('../views/SalesView.vue')
        },
        {
          path: 'data-gaps',
          name: 'data-gaps',
          component: () => import('../views/DataGapsView.vue')
        },
        {
          path: 'data-gaps/:dataGapIncidentId',
          name: 'data-gap-detail',
          component: () => import('../views/DataGapDetailView.vue')
        },
        {
          path: 'alerts',
          name: 'alerts',
          component: () => import('../views/AlertsView.vue')
        },
        {
          path: 'alerts/rules',
          name: 'alert-rules',
          component: () => import('../views/AlertRulesView.vue')
        },
        {
          path: 'notifications',
          name: 'notifications',
          component: () => import('../views/AlertRulesView.vue')
        },
        {
          path: 'consolidation',
          name: 'consolidation',
          component: () => import('../views/ConsolidationView.vue')
        },
        {
          path: 'onboarding',
          name: 'onboarding',
          component: () => import('../views/OnboardingView.vue')
        },
        {
          path: 'consolidation/reconsolidation',
          name: 'reconsolidation',
          component: () => import('../views/ReconsolidationView.vue')
        },
        {
          path: 'adjustments',
          name: 'adjustments',
          component: () => import('../views/AdjustmentsView.vue')
        },
        {
          path: 'mappings',
          name: 'mappings',
          component: () => import('../views/MappingsView.vue')
        },
        {
          path: 'mappings/:mappingId',
          name: 'mapping-detail',
          component: () => import('../views/MappingDetailView.vue')
        },
        {
          path: 'mappings/manual',
          name: 'mapping-manual',
          component: () => import('../views/ManualMappingView.vue')
        },
        {
          path: 'aggregated-reporting',
          name: 'aggregated-reporting',
          component: ManualReportingView
        },
        {
          path: 'calculations',
          name: 'calculations',
          component: () => import('../views/ContractTurnoverCalculationsView.vue')
        },
        {
          path: 'instructions',
          name: 'instructions',
          component: () => import('../views/InstructionsView.vue')
        },
        {
          path: 'account',
          name: 'account',
          component: AccountView
        },
        {
          path: 'finalize-period',
          name: 'finalize-period',
          component: () => import('../views/FinalizePeriodView.vue')
        },
        {
          path: 'invoicing',
          name: 'invoicing',
          component: () => import('../views/InvoicingView.vue')
        },
        {
          path: 'invoicing-history',
          name: 'invoicing-history',
          component: () => import('../views/InvoicingHistoryView.vue')
        },
        {
          path: 'contracts',
          component: h(RouterView),
          children: [
            {
              path: '',
              name: 'contracts',
              component: () => import('../views/ContractsView.vue')
            },
            {
              path: ':contractId',
              name: 'contract-detail',
              component: () => import('../views/ContractDetailView.vue')
            },
            {
              path: ':contractId/invoicing',
              name: 'contract-invoicing',
              component: () => import('../views/ContractInvoicingView.vue')
            }
          ]
        },
        {
          path: 'settings',
          component: h(RouterView),
          children: [
            {
              path: '',
              name: 'settings',
              component: SettingsView
            },
            {
              path: 'contacts',
              name: 'contacts',
              component: () => import('../views/GeneralContactsView.vue')
            },
            {
              path: 'integrators',
              name: 'integrators',
              component: () => import('../views/IntegratorsView.vue')
            },
            {
              path: 'locations',
              name: 'locations',
              component: () => import('../views/LocationsView.vue')
            },
            {
              path: 'shop-categories',
              name: 'shop-categories',
              component: () => import('../views/ShopCategoriesView.vue')
            },
            {
              path: 'pos-systems',
              name: 'pos-systems',
              component: () => import('../views/PosSystemsView.vue')
            },
            {
              path: 'sales-channels',
              name: 'sales-channels',
              component: () => import('../views/SalesChannelsView.vue')
            },
            {
              path: 'utility-meters',
              name: 'utility-meters',
              component: () => import('../views/UtilityMetersView.vue')
            },
            {
              path: 'status-types',
              name: 'status-types',
              component: () => import('../views/StatusTypeView.vue')
            },
            {
              path: 'logs',
              name: 'logs',
              component: () => import('../views/LogsView.vue')
            },
            {
              path: 'ftp-logs',
              name: 'ftp-logs',
              component: () => import('../views/FtpLogsView.vue')
            },
            {
              path: 'global-settings',
              name: 'global-settings',
              component: () => import('../views/GlobalSettingsView.vue')
            },
            {
              path: 'push-nodes',
              name: 'push-nodes',
              component: () => import('../views/PushNodesView.vue')
            },
            {
              path: 'jobs',
              name: 'jobs',
              component: () => import('../views/JobsView.vue')
            },
            {
              path: 'tools',
              name: 'tools',
              component: () => import('../views/ToolsView.vue')
            },
            {
              path: 'product-brands',
              name: 'product-brands',
              component: () => import('../views/ProductBrandsView.vue')
            },
            {
              path: 'users',
              name: 'users',
              component: () => import('../views/UsersView.vue')
            },
            {
              path: 'users/:userId',
              name: 'user-detail',
              component: () => import('../views/UserDetailView.vue')
            },
            {
              path: 'permission-sets',
              name: 'permission-sets',
              component: () => import('../views/PermissionSetsView.vue')
            },
            {
              path: 'audit-logs',
              name: 'audit-logs',
              component: () => import('../views/AuditLogView.vue')
            },
            {
              path: 'pax-settings',
              name: 'pax-settings',
              component: () => import('../views/PaxSettingsView.vue')
            },
            {
              path: 'companies',
              name: 'companies',
              component: () => import('../views/CompaniesView.vue')
            },
            {
              path: 'centers',
              name: 'centers',
              component: () => import('../views/CentersView.vue')
            }
          ]
        }
      ]
    },
    {
      path: '/auth',
      component: () => import('../layouts/AuthLayout.vue'),
      redirect: '/auth/login',
      meta: { anonymous: true },
      children: [
        {
          path: 'login',
          name: 'login',
          component: () => import('../views/LoginView.vue')
        },
        {
          path: 'callback',
          name: 'auth-callback',
          component: AuthCallbackView
        }
      ]
    },
    {
      path: '/service-mode',
      name: 'service-mode',
      component: ServiceModeLayout,
    },
    {
      path: '/cmd',
      component: h(RouterView),
      meta: { anonymous: true },
      children: [
        {
          path: 'success',
          name: 'Submit success',
          component: () => import('../views/CommandSuccessView.vue')
        },
        {
          path: 'error',
          name: 'CommandError',
          component: () => import('../views/CommandErrorView.vue')
        },
        {
          path: ':key?',
          name: 'Command',
          component: () => import('../views/CommandView.vue')
        }
      ]
    },
    {
      path: '/error',
      name: 'error',
      component: () => import('../layouts/ErrorLayout.vue'),
      meta: { anonymous: true }
    },
    {
      path: '/:pathMatch(.*)',
      component: () => import('../views/NotFoundView.vue')
    }
  ]
})

export default router
